import { userPatientAssignments } from '../services/api';

// Cache for user's accessible patients to avoid repeated API calls
let patientAccessCache = {
  userId: null,
  patients: [],
  lastFetch: null,
  cacheTimeout: 5 * 60 * 1000 // 5 minutes
};

/**
 * Get the current user from localStorage
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

/**
 * Check if the current user has access to all patients (admin/operations/demo roles)
 */
export const hasAdminAccess = (user = null) => {
  const currentUser = user || getCurrentUser();
  return currentUser && ['admin', 'operations', 'demo', 'super_admin'].includes(currentUser.role);
};

/**
 * Check if the current user is a supervisor or therapist with limited access
 */
export const hasLimitedAccess = (user = null) => {
  const currentUser = user || getCurrentUser();
  return currentUser && ['supervisor', 'therapist'].includes(currentUser.role);
};

/**
 * Get all patients the current user has access to
 */
export const getUserAccessiblePatients = async (forceRefresh = false) => {
  const currentUser = getCurrentUser();
  
  if (!currentUser) {
    return [];
  }

  // Admin users have access to all patients
  if (hasAdminAccess(currentUser)) {
    // For admin users, we could fetch all patients, but we'll let the calling code handle this
    // to avoid importing the patients API here and creating circular dependencies
    return 'ALL_PATIENTS';
  }

  // Check cache first
  const now = Date.now();
  const cacheValid = patientAccessCache.userId === currentUser.id && 
                     patientAccessCache.lastFetch &&
                     (now - patientAccessCache.lastFetch) < patientAccessCache.cacheTimeout;

  if (!forceRefresh && cacheValid) {
    return patientAccessCache.patients;
  }

  try {
    // Fetch assigned patients for supervisor/therapist
    if (hasLimitedAccess(currentUser)) {
      const response = await userPatientAssignments.getUserAccessiblePatients(currentUser.id);
      const patients = response.data || [];
      
      // Update cache
      patientAccessCache = {
        userId: currentUser.id,
        patients: patients,
        lastFetch: now,
        cacheTimeout: patientAccessCache.cacheTimeout
      };
      
      return patients;
    }
  } catch (error) {
    console.error('Error fetching user accessible patients:', error);
  }

  // Default to empty array for other roles
  return [];
};

/**
 * Check if the current user has access to a specific patient
 */
export const hasPatientAccess = async (patientId, user = null) => {
  const currentUser = user || getCurrentUser();
  
  if (!currentUser || !patientId) {
    return false;
  }

  // Admin users have access to all patients
  if (hasAdminAccess(currentUser)) {
    return true;
  }

  // For supervisor/therapist, check if patient is in their assignments
  if (hasLimitedAccess(currentUser)) {
    try {
      const accessiblePatients = await getUserAccessiblePatients();
      return accessiblePatients.some(patient => patient.id === patientId);
    } catch (error) {
      console.error('Error checking patient access:', error);
      return false;
    }
  }

  // Default deny for other roles
  return false;
};

/**
 * Clear the patient access cache (useful after role changes, logout, etc.)
 */
export const clearPatientAccessCache = () => {
  patientAccessCache = {
    userId: null,
    patients: [],
    lastFetch: null,
    cacheTimeout: 5 * 60 * 1000
  };
};

/**
 * Redirect to unauthorized page or dashboard if user doesn't have patient access
 */
export const redirectIfNoAccess = async (patientId, navigate) => {
  const hasAccess = await hasPatientAccess(patientId);
  
  if (!hasAccess) {
    console.warn(`User does not have access to patient ${patientId}`);
    navigate('/dashboard', { 
      replace: true,
      state: { 
        error: 'Você não tem permissão para acessar este paciente.' 
      }
    });
    return false;
  }
  
  return true;
};

/**
 * Hook to use patient access in React components
 */
export const usePatientAccess = () => {
  const currentUser = getCurrentUser();
  
  return {
    currentUser,
    hasAdminAccess: hasAdminAccess(currentUser),
    hasLimitedAccess: hasLimitedAccess(currentUser),
    getUserAccessiblePatients,
    hasPatientAccess,
    clearPatientAccessCache,
    redirectIfNoAccess
  };
};